/**
 * Phase J.3.2 — single source of truth for the notifications hook used by
 * the bell, the list pages, and the preferences pages.
 *
 * - useUnreadCount({ role }): polls /api/{role}/notifications/unread-count
 *   every 8s while the tab is visible. Pauses on `visibilitychange` →
 *   `hidden`, resumes (with an immediate refresh) on `visible`.
 * - useNotificationsList({ role, read, limit }): fetches a page lazily
 *   (does NOT poll). Returns items + helpers (markRead / markAllRead /
 *   loadMore). The bell calls `refresh()` ON OPEN; the full list pages
 *   call it on filter / mount.
 * - usePreferences({ role }): GET on mount + a `patch(eventType, flags)`
 *   helper that PATCHes only the changed key.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";

export const NOTIF_POLL_MS = 8000;
const BASE = (role) => (role === "customer" ? "/customer" : "/admin");


/** Tab-visibility helper — true while the page is visible (or unknown
 * because the API isn't supported). */
function _isTabVisible() {
    if (typeof document === "undefined" || typeof document.visibilityState === "undefined") {
        return true;
    }
    return document.visibilityState === "visible";
}


export function useUnreadCount({ role, pollMs = NOTIF_POLL_MS }) {
    const [count, setCount] = useState(0);
    const [error, setError] = useState(null);
    const timerRef = useRef(null);
    const aliveRef = useRef(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get(`${BASE(role)}/notifications/unread-count`);
            if (!aliveRef.current) return;
            setCount(Number(data?.count ?? 0));
            setError(null);
        } catch (e) {
            if (!aliveRef.current) return;
            setError(formatApiError(e));
        }
    }, [role]);

    useEffect(() => {
        aliveRef.current = true;
        // Immediate fetch on mount.
        refresh();
        function schedule() {
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(async () => {
                if (_isTabVisible()) {
                    await refresh();
                }
                schedule();
            }, pollMs);
        }
        schedule();
        function onVisibility() {
            if (_isTabVisible()) {
                // Immediate refresh on returning to the tab.
                refresh();
            }
        }
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            aliveRef.current = false;
            clearTimeout(timerRef.current);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [refresh, pollMs]);

    return { count, error, refresh, setCount };
}


export function useNotificationsList({ role, read = "all", limit = 20 }) {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [cursor, setCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchPage = useCallback(async ({ append = false, cursorOverride } = {}) => {
        setLoading(true);
        try {
            const params = { read, limit };
            const cur = cursorOverride === undefined ? null : cursorOverride;
            if (cur) params.cursor = cur;
            const { data } = await api.get(`${BASE(role)}/notifications`, { params });
            setItems((prev) => (append ? [...prev, ...(data.items || [])] : (data.items || [])));
            setCursor(data.next_cursor || null);
            setUnread(Number(data.unread_count ?? 0));
            setError(null);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [role, read, limit]);

    useEffect(() => {
        fetchPage({ append: false, cursorOverride: null });
    }, [fetchPage]);

    const refresh = useCallback(() => fetchPage({ append: false, cursorOverride: null }), [fetchPage]);
    const loadMore = useCallback(() => {
        if (cursor) fetchPage({ append: true, cursorOverride: cursor });
    }, [cursor, fetchPage]);

    const markRead = useCallback(async (id) => {
        try {
            const { data } = await api.post(`${BASE(role)}/notifications/${id}/read`);
            // Local optimistic patch — replace by id, decrement unread.
            setItems((prev) => prev.map((n) => (n.id === id ? data : n)));
            setUnread((n) => Math.max(0, n - 1));
        } catch (e) {
            setError(formatApiError(e));
        }
    }, [role]);

    const markAllRead = useCallback(async () => {
        try {
            await api.post(`${BASE(role)}/notifications/mark-all-read`);
            await refresh();
        } catch (e) {
            setError(formatApiError(e));
        }
    }, [role, refresh]);

    return {
        items, unread, cursor, loading, error,
        refresh, loadMore, markRead, markAllRead,
    };
}


export function usePreferences({ role }) {
    const [prefs, setPrefs] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [savingKey, setSavingKey] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`${BASE(role)}/notifications/preferences`);
            setPrefs(data.preferences || {});
            setCatalog(data.catalog || Object.keys(data.preferences || {}));
            setError(null);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [role]);

    useEffect(() => { load(); }, [load]);

    const patchOne = useCallback(async (eventType, flags) => {
        setSavingKey(eventType);
        // Optimistic update so the toggle "snaps" immediately.
        setPrefs((prev) => ({ ...(prev || {}), [eventType]: { ...((prev || {})[eventType] || {}), ...flags } }));
        try {
            const { data } = await api.patch(`${BASE(role)}/notifications/preferences`, {
                preferences: { [eventType]: flags },
            });
            setPrefs(data.preferences || {});
            setError(null);
        } catch (e) {
            setError(formatApiError(e));
            // Re-load on failure to undo the optimistic patch.
            await load();
        } finally {
            setSavingKey(null);
        }
    }, [role, load]);

    return { prefs, catalog, loading, error, savingKey, refresh: load, patchOne };
}
